import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { settingsService, WebsiteSettings, defaultSettings } from '../services/settingsService';

interface SettingsContextType {
  settings: WebsiteSettings;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  updateSettings: (newSettings: Partial<WebsiteSettings>) => Promise<void>;
  updatePageVisibility: (page: keyof WebsiteSettings['pageVisibility'], isVisible: boolean) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<WebsiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await settingsService.getSettings();
      setSettings(data);
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const refreshSettings = async () => {
    await loadSettings();
  };

  const updateSettings = async (newSettings: Partial<WebsiteSettings>) => {
    try {
      const updatedSettings = await settingsService.updateSettings(newSettings);
      setSettings(updatedSettings);
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to update settings.');
      throw err;
    }
  };

  const updatePageVisibility = async (page: keyof WebsiteSettings['pageVisibility'], isVisible: boolean) => {
    try {
      const updatedSettings = await settingsService.updatePageVisibility(page, isVisible);
      setSettings(updatedSettings);
    } catch (err) {
      console.error(`Error updating visibility for ${page}:`, err);
      setError(`Failed to update visibility for ${page}.`);
      throw err;
    }
  };

  const value = {
    settings,
    loading,
    error,
    refreshSettings,
    updateSettings,
    updatePageVisibility,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext; 